/**
 * The calendar date (`YYYY-MM-DD`) an instant falls on in an IANA zone — the
 * org's day, never the browser's or UTC's. 21:30 in Phoenix is 04:30 UTC the
 * next day; a date printed from it, or a due date counted from it, starts on
 * the Phoenix date, as the server's org-local date math does. A stored
 * `performed_at` is dated the same way: the server stores a date-only answer at
 * 00:00 of its day on the org's calendar, so that day is the one this reads.
 */
export function orgLocalDate(instant: string | Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(typeof instant === "string" ? new Date(instant) : instant);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The calendar date (`YYYY-MM-DD`) a raw answer names before it is sent — a
 * performed answer read straight off a form. The value's shape decides, never
 * its clock: a date-only string (`YYYY-MM-DD`, a date item's answer) is that
 * calendar day in every zone, and anything else is an instant, which takes the
 * org's local day through `orgLocalDate` — an instant at exactly UTC midnight
 * included, like every other.
 */
export function calendarDay(value: string, timezone: string): string {
  return DATE_ONLY.test(value) ? value : orgLocalDate(value, timezone);
}

/** Today on the org's calendar. `now` is the caller's clock, so a test pins it. */
export function orgLocalToday(timezone: string, now: Date): string {
  return orgLocalDate(now, timezone);
}
